import React from 'react';

import { trackReferralShared } from '@elara/client-core/lib/analytics-events';

import { useToastState, useUserProfileState } from '#states/hooks.js';
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, MaterialIcon } from '#ui/index.js';

function CopyField({ label, value }: { label: string; value: string }) {
  const toastState = useToastState();

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(value);
      trackReferralShared({ channel: 'copy_link' });
      toastState.addToast({ title: 'Copied!', variant: 'success' });
    } catch {
      toastState.addToast({ title: 'Failed to copy', variant: 'destructive' });
    }
  }

  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      <div className="flex items-center gap-2">
        <div className="flex-1 rounded-md border bg-muted/50 px-3 py-2 text-sm font-mono select-all break-all">
          {value}
        </div>
        <Button variant="outline" size="icon" onClick={handleCopy} className="shrink-0">
          <MaterialIcon icon="content_copy" />
        </Button>
      </div>
    </div>
  );
}

export function ReferralDialog(): React.ReactNode {
  const userProfileState = useUserProfileState();

  if (!userProfileState.isReferralDialogOpen) return undefined;

  return (
    <Dialog
      open={userProfileState.isReferralDialogOpen}
      onOpenChange={(open) => !open && userProfileState.closeReferralDialog()}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Know someone who would love Elara?</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground text-pretty">
          Friends and family who sign up via your referral link will be fast-tracked on the waitlist.
        </p>
        {userProfileState.isReferralLoading ? (
          <div className="flex items-center justify-center py-6">
            <p className="text-sm text-muted-foreground">Loading your referral code...</p>
          </div>
        ) : userProfileState.referralCode && userProfileState.referralUrl ? (
          <div className="space-y-4">
            <CopyField label="Referral link" value={userProfileState.referralUrl} />
          </div>
        ) : (
          <div className="flex items-center justify-center py-6">
            <p className="text-sm text-destructive">Failed to load referral code. Please try again.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
